import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { apiGet, apiPost, apiPut } from "../../lib/api";
import { useTenantAuth } from "../../context/auth";

type Rule = {
  id: string;
  name: string;
  trigger: string;
  offset_minutes: number;
  channel: string;
  enabled: boolean;
};

type Flag = {
  id: string;
  key: string;
  enabled: boolean;
};

export function ManagerAutomationPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { bootstrap } = useTenantAuth();
  const [name, setName] = useState("Reminder");
  const [template, setTemplate] = useState("Reminder for {{customer_name}} at {{start_at}}");
  const [offsetMinutes, setOffsetMinutes] = useState(-60);
  const [flagKey, setFlagKey] = useState("public_booking");
  const [flagEnabled, setFlagEnabled] = useState(true);

  const rules = useQuery({
    queryKey: ["manager-automation-rules"],
    queryFn: () => apiGet<{ items: Rule[] }>("/v3/automation/rules"),
  });
  const flags = useQuery({
    queryKey: ["manager-automation-flags"],
    queryFn: () => apiGet<{ items: Flag[] }>("/v3/automation/flags"),
  });

  const createRule = useMutation({
    mutationFn: () =>
      apiPost("/v3/automation/rules", {
        branch_id: null,
        name,
        trigger: "before_appointment",
        offset_minutes: offsetMinutes,
        channel: "telegram",
        template,
        enabled: true,
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["manager-automation-rules"] });
    },
  });

  const upsertFlag = useMutation({
    mutationFn: () => apiPut(`/v3/automation/flags/${encodeURIComponent(flagKey)}`, { key: flagKey, enabled: flagEnabled }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["manager-automation-flags"] });
      await qc.invalidateQueries({ queryKey: ["tenant-bootstrap"] });
    },
  });

  return (
    <div className="grid gap-4">
      <h1 className="text-2xl font-semibold">{t("manager.automation.title")}</h1>
      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.automation.section.rules")}</h2>
        <div className="grid gap-2 mt-2 sm:grid-cols-2 lg:grid-cols-3">
          <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
          <input className="input" type="number" value={offsetMinutes} onChange={(e) => setOffsetMinutes(Number(e.target.value || "0"))} />
          <button className="btn w-full sm:w-auto" onClick={() => createRule.mutate()}>
            {t("manager.automation.action.addRule")}
          </button>
        </div>
        <textarea className="input mt-2" rows={3} value={template} onChange={(e) => setTemplate(e.target.value)} />
        <div className="grid gap-2 mt-3">
          {(rules.data?.items ?? []).map((r) => (
            <article key={r.id} className="rounded-lg border border-slate-200 p-3 bg-white">
              <p className="font-semibold">{r.name}</p>
              <p className="text-xs text-slate-500">
                {r.trigger} {r.offset_minutes}m | {r.channel} | {r.enabled ? t("common.enabled") : t("common.disabled")}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="panel p-4">
        <h2 className="text-lg font-semibold">{t("manager.automation.section.flags")}</h2>
        <p className="text-sm text-slate-600 mt-1">{t("manager.automation.currentLocale", { locale: bootstrap?.locale || "-" })}</p>
        <div className="grid gap-2 mt-2 sm:grid-cols-2 lg:grid-cols-3">
          <input className="input" value={flagKey} onChange={(e) => setFlagKey(e.target.value)} />
          <select className="input" value={flagEnabled ? "1" : "0"} onChange={(e) => setFlagEnabled(e.target.value === "1")}>
            <option value="1">{t("common.enabled")}</option>
            <option value="0">{t("common.disabled")}</option>
          </select>
          <button className="btn secondary w-full sm:w-auto" onClick={() => upsertFlag.mutate()}>
            {t("manager.automation.action.upsertFlag")}
          </button>
        </div>
        <div className="grid gap-2 mt-3">
          {(flags.data?.items ?? []).map((f) => (
            <article key={f.id} className="rounded-lg border border-slate-200 p-2 bg-white text-sm">
              {f.key}: <strong>{f.enabled ? t("common.enabled") : t("common.disabled")}</strong>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

